import { EnterOutlined } from '@ant-design/icons';
import { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Input from '@/components/Inputs/Input';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { track } from '@/utils/track/track';
import { isEmptyString, timeOut } from '@/utils/util';

import TaxonomyContext from '../../../../context/TaxonomyContext';
import AddedItem from '../AiTopic/AddedItem';
import S from '../styles';
import type { CreateTopicModalProps } from '../types';
import UpperLevelDisplay from '../UpperLevelDisplay/UpperLevelDisplay';

const CreateManualTopicContainer = <T extends CreateTopicTarget>(
  props: CreateTopicModalProps<T> & {
    closeModal: () => void;
  },
) => {
  const { taxonomyAlias } = useContext(TaxonomyContext);
  if (!taxonomyAlias) throw new Error('TaxonomyAlias is missing');
  const fetchTaxnomyTopics = useTaxonomyStore(
    (state) => state.fetchTaxonomyTopics,
  );

  const color = useColor();
  const [topicList, setTopicList] = useState<string[]>([]);

  const submitReq = useMemo(() => {
    switch (props.target) {
      case 'taxonomy':
        return service.taxonomy.createTopicUnderTaxonomy;
      case 'topic':
        return service.taxonomy.createTopicUnderTopic;
      default:
        throw new Error('Invalid create topic target');
    }
  }, [props.target]);

  const onSubmit = async () => {
    track(AMP_KEYS.TAXONOMY_CREATE_TOPIC_DONE, {
      type: TOPIC_2_TYPE.MANUAL,
    });
    await submitReq(
      props.targetAlias,
      topicList.map((t) => ({
        name: t,
        category: TOPIC_2_TYPE.MANUAL,
      })),
    );
    fetchTaxnomyTopics(taxonomyAlias);
    props.closeModal();
  };

  return (
    <Style>
      <S.MainLayout style={{ overflow: 'auto' }}>
        <VFlex className="header">
          <Heading type="h2">Add manual topics</Heading>
        </VFlex>
        <Divider />
        <VFlex className="body" g={24} style={{ overflow: 'auto' }}>
          <UpperLevelDisplay
            target={props.target}
            targetTopicAlias={props.targetAlias}
            taxonomyAlias={props.taxonomyAlias}
          />
          <Input
            placeholder="Type topic name"
            sizeType="l"
            wrapperStyle={{ width: 324 }}
            onEnter={async (value, e) => {
              if (isEmptyString(value)) {
                await timeOut(100);
                (e.target as HTMLInputElement).focus();
                return;
              }
              setTopicList((p) => {
                return [...p, value];
              });
              e.currentTarget.value = '';
              await timeOut(100);
              (e.target as HTMLInputElement).focus();
            }}
            rightIcon={
              <IconButton
                icon={<EnterOutlined style={{ color: color.gray500 }} />}
              />
            }
          />
          <SelectedArea f={1}>
            <Flex j="space-between">
              <Typo type="label1" weight="m" className="text-primary-100">
                {topicList.length} added
              </Typo>
            </Flex>
            <Space h={12} />
            <VFlex className="list-box" f={1}>
              {topicList.length > 0 && (
                <Flex g={8} style={{ flexWrap: 'wrap' }}>
                  {topicList.map((item, index) => (
                    <AddedItem
                      key={index}
                      name={item}
                      onDeleteClick={() => {
                        const newArr = [...topicList];
                        newArr.splice(index, 1);
                        setTopicList(newArr);
                      }}
                    />
                  ))}
                </Flex>
              )}
            </VFlex>
          </SelectedArea>
        </VFlex>
        <Divider />
        <Flex className="footer" j="flex-end">
          <Button variant="ghost" sizeType="l" onClick={props.closeModal}>
            Cancel
          </Button>
          <Button variant="primary" sizeType="l" onClick={onSubmit}>
            Done
          </Button>
        </Flex>
      </S.MainLayout>
    </Style>
  );
};

const Style = styled(Flex)`
  width: 800px;
  height: calc(100vh - 64px);
  max-height: 780px;
  overflow: auto;
`;

const SelectedArea = styled(VFlex)`
  padding: 24px;
  padding-bottom: 0;
  padding-right: 0;
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: ${(props) => props.theme.radius.card}px;
  overflow: auto;
  .list-box {
    overflow: auto;
    padding: 12px 24px 24px 0px;
  }
`;

export default CreateManualTopicContainer;
